import type { ProfileResource } from '@medplum/core';

/**
 * Role helpers for the patient portal (issue #153 / #154).
 *
 * The portal's ROLE/UI isolation turns on one fact: a portal session's Medplum
 * profile is a `Patient` (clinician sessions are `Practitioner`). These pure
 * predicates back both route guards — `RequirePatientAuth` (portal) and the
 * clinician `RequireAuth` (which bounces a Patient profile out of clinician UI).
 * Data isolation is a separate, server-side concern (see `patientAccessPolicy`).
 */

/** True only for a signed-in Patient profile. Clinician (Practitioner) / RelatedPerson / undefined → false. */
export function isPatientProfile(profile: ProfileResource | undefined | null): boolean {
  return profile?.resourceType === 'Patient';
}

/** True for a signed-in non-patient profile (e.g. a clinician). Undefined (signed out) → false. */
export function isNonPatientProfile(profile: ProfileResource | undefined | null): boolean {
  return !!profile && profile.resourceType !== 'Patient';
}

/** Best-effort display name for the portal chrome ("Signed in" when a name is absent). */
export function patientDisplayName(profile: ProfileResource | undefined | null): string {
  const name = profile && 'name' in profile ? profile.name?.[0] : undefined;
  if (!name) {
    return 'Signed in';
  }
  const given = name.given?.join(' ') ?? '';
  return [given, name.family].filter(Boolean).join(' ').trim() || 'Signed in';
}
